import { IonRouterLink } from '@ionic/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Button from '../../components/actions/Button'
import useUser from '../../contexts/hooks/useUser'
import { backendCall } from '../../modules/backendApi'
import PublicWrapper from '../../wrappers/publicWrapper/PublicWrapper'
import LoadingPage from '../system/LoadingPage'

interface FormData {
	email: string
	password: string
	passwordConfirmation: string
}

const AccountRegistration: React.FC = () => {
	const { t } = useTranslation('pages_createUser')

	const { user, setUser } = useUser()
	const history = useHistory()

	const [formData, setFormData] = useState<FormData>({
		email: '',
		password: '',
		passwordConfirmation: '',
	})
	const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})
	const [loading, setLoading] = useState(false)

	const createUserHandler = async () => {
		// Reset previous errors
		setErrors({})

		// Validate form
		const validationErrors: Partial<Record<keyof FormData, string>> = {}

		// Email validation
		const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
		if (!formData.email) {
			validationErrors.email = t('pakollinen', { ns: 'common' })
		} else if (!emailRegex.test(formData.email)) {
			validationErrors.email = t('emailVirheellinen')
		}

		// Password validation
		if (!formData.password) {
			validationErrors.password = t('pakollinen', { ns: 'common' })
		} else if (formData.password.length < 8) {
			validationErrors.password = t('salasananPituus')
		}

		// Password confirmation validation
		if (!formData.passwordConfirmation) {
			validationErrors.passwordConfirmation = t('pakollinen', { ns: 'common' })
		} else if (formData.password !== formData.passwordConfirmation) {
			validationErrors.passwordConfirmation = t('salasanaVirhe')
		}

		// If there are validation errors, stop submission
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors)
			return
		}

		setLoading(true)

		try {
			if (!user) {
				console.error('User context is not available')
				return
			}
			const { email, password } = formData

			// Check email availability
			const checkEmailAvailability = await backendCall.users.checkEmailAvailability(email)
			if (!checkEmailAvailability) {
				setErrors({ email: t('emailVarattu') })
				setLoading(false)
				return
			}

			setUser({
				email,
				password,
			})

			history.push(`/overview`)
		} catch (error) {
			console.error('User creation failed', error)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (user && user.email && user.email.includes('@')) {
			history.push('/overview')
		}
	}, [user])

	if (loading) return <LoadingPage />

	return (
		<PublicWrapper id='create-user'>
			<h1 className='text-text-dark dark:text-white text-xl text-center'>{t('luo')}</h1>
			<form>
				<div className='flex flex-col mt-6'>
					<label className='font-semibold text-xs uppercase tracking-wider text-[#52ae32] dark:text-[#3cb49f]'>
						{t('email')}
					</label>
					<input
						className='rounded-lg text-base sm:text-lg w-full mt-1 px-2 sm:px-6 py-2 bg-[#cae3cc] border-2 border-[#52ae32] dark:bg-[#acd7d4] dark:border-[#3cb49f] text-text-dark'
						type='email'
						value={formData.email}
						onChange={e => {
							const value = e.target.value.trim()
							setFormData(prev => ({ ...prev, email: value }))
						}}
					/>
					{errors.email && <div className='text-red-500'>{errors.email}</div>}
				</div>

				<div className='flex flex-col mt-6'>
					<label className='font-semibold text-xs uppercase tracking-wider text-[#52ae32] dark:text-[#3cb49f]'>
						{t('salasana')}
					</label>
					<input
						className='rounded-lg text-base sm:text-lg w-full mt-1 px-2 sm:px-6 py-2 bg-[#cae3cc] border-2 border-[#52ae32] dark:bg-[#acd7d4] dark:border-[#3cb49f] text-text-dark'
						type='password'
						value={formData.password}
						onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
					/>
					{errors.password && <div className='text-red-500'>{errors.password}</div>}
				</div>

				<div className='flex flex-col mt-6'>
					<label className='font-semibold text-xs uppercase tracking-wider text-[#52ae32] dark:text-[#3cb49f]'>
						{t('vahvistaSalasana')}
					</label>
					<input
						className='rounded-lg text-base sm:text-lg w-full mt-1 px-2 sm:px-6 py-2 bg-[#cae3cc] border-2 border-[#52ae32] dark:bg-[#acd7d4] dark:border-[#3cb49f] text-text-dark'
						type='password'
						value={formData.passwordConfirmation}
						onChange={e => setFormData(prev => ({ ...prev, passwordConfirmation: e.target.value }))}
					/>
					{errors.passwordConfirmation && (
						<div className='text-red-500'>{errors.passwordConfirmation}</div>
					)}
				</div>

				<div className='buttons-container mt-2 flex flex-col gap-4 justify-center items-center'>
					<Button
						onClick={createUserHandler}
						className='submit bg-brand-primary-100-light dark:bg-brand-primary-100-dark text-white'
						disabled={loading}
					>
						{t('tallenna', { ns: 'common' })}
					</Button>
					<IonRouterLink
						routerLink='/'
						className='text-brand-secondary-100-light dark:text-brand-secondary-100-dark'
					>
						{t('peruuta', { ns: 'common' })}
					</IonRouterLink>
				</div>
			</form>
		</PublicWrapper>
	)
}

export default AccountRegistration
